/**
 * Wishlist → Add to Bag.
 *
 * The side effects that fire when a size is picked from the wishlist used to
 * live inline in `EditSizeUpdate.setProductSize` (the `wishlistPage` branch).
 * They are extracted here so the desktop multi-seller Size/Quantity modal
 * (`ProductUpdationPopup` opened from the wishlist card) runs the exact same
 * flow — same events, same imsBatchId/commercialType resolution, same
 * addToCart call — instead of a second copy of it.
 */
import _get from 'lodash/get';
import { sendEvent } from '../../utils/dataGrinch.js';
import { getPageInfo } from '../../utils/pageInfo.js';
import { gtmEventPush, luxeGtmAddToCart, gtmWishlistAddToBagClicked } from '../../modules/ga/GTMdatalayer';
import { jioAdsAddtoCart } from '../../utils/jioAds';
import { buildAtbPdRushData } from '../../edd/utils/buildAtbPdRushData';
import { fetchSizeAndBrandSize, beautyCheck } from '../../utils/StandardSizeUtils';

/** Same UA sniff the inline flow used to pick the addToCart deviceType. */
export function getAddToBagDeviceType() {
  if (
    typeof navigator !== 'undefined' &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  ) {
    return 'mobile';
  }
  return 'desktop';
}

/** imsBatchId of the selected sku, read off the colour group's variantOptions. */
export function getImsBatchId(variantOptions = [], code) {
  let batchId;
  if (_get(variantOptions, 'length') > 0) {
    const sizeVariantOptionsArr = variantOptions.filter(variant => variant.code === code);
    const variantOptionQualifiers =
      _get(sizeVariantOptionsArr, '[0].variantOptionQualifiers') || [];
    variantOptionQualifiers.forEach(variant => {
      if (variant.qualifier === 'imsBatchId') {
        batchId = variant.value;
      }
    });
  }
  return batchId;
}

/**
 * "add to cart from closet" — fired when the wishlist size list is shown, with
 * the sizes that are out of stock. Callers pass the already-mapped size list so
 * the label derivation stays in one place.
 */
export function pushWishlistSizeListEvent(optionCode = '', sizes = []) {
  if (!sizes.length) return;
  const OOSSizes = sizes.reduce((result, eachValue) => {
    if (eachValue.stockLevel === 0) {
      result.push(eachValue.value);
    }
    return result;
  }, []);
  gtmEventPush({
    event: 'add_to_cart_from_closet',
    event_category: 'closet interaction',
    event_action: 'add to cart',
    event_label: optionCode,
    out_of_stock_sizes: OOSSizes.join(),
    out_of_stock_sizes_count: OOSSizes.length
  });
}

/**
 * Size variant options → `{ code, value, stockLevel, priceDataFormattedValue }`,
 * using the same label precedence as the size swatches (beauty volume → brand
 * size → raw size qualifier).
 */
export function mapWishlistSizeOptions(sizeVariantOptions = [], newSizeRevampFlag) {
  return sizeVariantOptions.map(option => {
    const {
      code,
      stock: { stockLevel } = {},
      variantOptionQualifiers = [],
      priceData: { formattedValue: priceDataFormattedValue = '' } = {}
    } = option || {};
    const { valueWithUnit: beauty } = beautyCheck(variantOptionQualifiers, true);
    const { Size = '' } = fetchSizeAndBrandSize(variantOptionQualifiers, newSizeRevampFlag);
    let sizeName = '';
    (variantOptionQualifiers || []).forEach(({ qualifier, value }) => {
      if (qualifier === 'standardSize' && value) {
        sizeName = value;
      } else if (qualifier === 'size' && !sizeName) {
        sizeName = value;
      }
    });
    return {
      code,
      stockLevel,
      priceDataFormattedValue,
      value: beauty || Size || sizeName
    };
  });
}

/** Luxe GA add-to-cart push for a wishlist item. */
export function pushWishlistLuxeAddToCart({
  entity = {},
  selectedSizeCode = '',
  newDesignLuxe = false,
  screenName = '',
  screenType = '',
  enableMultiSellerSla = false,
  // B2C runs with the multiseller flag off and reports the same seller /
  // delivery props on the event.
  enableB2cSeller = false,
  slaOptions = null,
  selectedPriceType = '',
  /** The picked card, when the host has one — see `addToBagFromWishlist`. */
  selectedSlaOption = null,
  previousScreenName = 'wishlist screen'
}) {
  if (!newDesignLuxe) return;
  const {
    code,
    brickCategory,
    brickName,
    exclusiveTill,
    fnlColorVariantData: { brandName = '' } = {},
    price: { value } = {},
    wasPriceData: { value: price = '' } = {},
    name,
    discountPercent = '',
    segmentId = '',
    experimentId = ''
  } = entity;
  const tag = exclusiveTill ? 'Exclusive' : '';
  luxeGtmAddToCart(
    name,
    code?.split('_')[0],
    value,
    brandName,
    brickCategory,
    selectedSizeCode,
    code?.split('_')[1],
    discountPercent,
    brickName,
    tag,
    1,
    screenName,
    'luxe',
    screenType,
    screenName,
    screenName,
    'luxe',
    true,
    '',
    segmentId,
    experimentId,
    price,
    '',
    '',
    '',
    '',
    selectedSizeCode,
    false,
    {},
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    '',
    {},
    0,
    false,
    false,
    false,
    {
      enableMultiSellerSla,
      enableB2cSeller,
      slaOptions,
      selectedPriceType,
      selectedSlaOption,
      previousScreenName
    }
  );
}

/**
 * Run the wishlist add-to-bag flow for a selected sku.
 *
 * `addProductToCart` is supplied by the caller so this module stays free of
 * redux — both hosts bind it to the same `addToCart` action.
 */
export function addToBagFromWishlist({
  entity = {},
  code,
  value,
  priceDataFormattedValue = '',
  selectedSizeCode,
  quantity = 1,
  newDesignLuxe = false,
  screenName = '',
  screenType = '',
  addProductToCart,
  // Multiseller "Add To Bag Clicked" enrichment (non-luxe wishlist path).
  enableMultiSellerSla = false,
  // B2C rides the same scopedView options, so it prices the line the same way —
  // and additionally names the seller the picked card belongs to.
  b2cSellerEnabled = false,
  selectedSellerId = '',
  slaOptions = null,
  selectedPriceType = '',
  /**
   * The raw option the add speaks for, when the host has one — the B2C seller
   * list's pick. It is not in `slaOptions` (that list is a separate
   * fetchAllSellers response), so the event has no other way to name the seller.
   */
  selectedSlaOption = null,
  previousScreenName = 'closet screen',
  eddDetails = {}
}) {
  const { variantOptions, commercialType = '' } = entity || {};
  const deviceType = getAddToBagDeviceType();

  const eventObj = [];
  const { pageId, pageType } = getPageInfo(location.pathname || '');
  eventObj.pageId = pageId;
  eventObj.pageType = pageType;
  eventObj.productId = code;
  eventObj.size = value;
  eventObj.Quantity = quantity;
  eventObj.Price = priceDataFormattedValue;
  sendEvent('AddToBagFromCloset', eventObj);

  pushWishlistLuxeAddToCart({
    entity,
    selectedSizeCode: selectedSizeCode || code,
    newDesignLuxe,
    screenName,
    screenType,
    enableMultiSellerSla,
    enableB2cSeller: b2cSellerEnabled,
    slaOptions,
    selectedPriceType,
    selectedSlaOption,
    previousScreenName
  });

  // Luxe already fires "Add To Bag Clicked" via luxeGtmAddToCart above; the
  // non-luxe wishlist flow never did, so fire it here.
  if (!newDesignLuxe) {
    const pdRushData = buildAtbPdRushData({
      eddDetails,
      slaOptions,
      selectedSlaOption
    });
    gtmWishlistAddToBagClicked({
      entity,
      size: value,
      quantity,
      screenName,
      previousScreenName,
      enableMultiSellerSla,
      enableB2cSeller: b2cSellerEnabled,
      slaOptions,
      selectedPriceType,
      selectedSlaOption,
      pdRushData
    });
  }

  const batchId = getImsBatchId(variantOptions, code);
  const slaPriceType = enableMultiSellerSla || b2cSellerEnabled ? selectedPriceType : '';
  const sellerId = b2cSellerEnabled ? selectedSellerId : '';
  if (batchId || commercialType) {
    addProductToCart(
      code,
      deviceType,
      '',
      '',
      batchId,
      commercialType,
      quantity,
      slaPriceType,
      sellerId
    );
  } else {
    addProductToCart(
      code,
      deviceType,
      undefined,
      undefined,
      undefined,
      undefined,
      quantity,
      slaPriceType,
      sellerId
    );
  }

  const { productCode, brickCategory, brickSubCategory, brickName, price: { value: price } = {} } =
    entity || {};
  jioAdsAddtoCart({
    brickCategory,
    brickSubCategory,
    brickName,
    code: productCode,
    price,
    quantity
  });
}
